import React, { useEffect } from 'react';
import { View } from '@adobe/react-spectrum';
import { Rectangle } from '@printu/geom';
import { EditorActions } from 'app/stores/editor/actions';
import { useAppDispatch } from 'app/stores/store';
import { SVG } from 'app/views/components/svg/SVG';
import { useConnect } from 'app/views/hooks/useConnect';
import { useHandleDrag } from 'app/views/hooks/useHandleDrag';
import { toPx } from 'app/utils/DOM';
import { call } from 'app/utils/function';
import { Handles } from './Handles';
export const InlineCropController = (props) => {
    const { stateKey, crop, designRect, onChange } = props;
    const dispatch = useAppDispatch();
    const dragData = React.useRef();
    const containerRef = React.useRef(null);
    const stateProps = useConnect(state => {
        var _a, _b;
        const stateCrop = stateKey
            ? (_a = state.editor.controllerData) === null || _a === void 0 ? void 0 : _a[stateKey]
            : undefined;
        const unifiedCropArea = (_b = stateCrop !== null && stateCrop !== void 0 ? stateCrop : crop) !== null && _b !== void 0 ? _b : { x: 0, y: 0, width: 1, height: 1 };
        return {
            unifiedCropArea,
        };
    });
    const { unifiedCropArea } = stateProps;
    const getCropRect = React.useCallback(() => new Rectangle(designRect.width * unifiedCropArea.x, designRect.height * unifiedCropArea.y, designRect.width * unifiedCropArea.width, designRect.height * unifiedCropArea.height).toObject(), [designRect, unifiedCropArea]);
    const setRect = React.useCallback((rect) => {
        const unifiedRect = new Rectangle(rect.x / designRect.width, rect.y / designRect.height, rect.width / designRect.width, rect.height / designRect.height).toObject();
        if (stateKey) {
            dispatch(EditorActions.setControllerData({
                key: stateKey,
                value: unifiedRect,
            }));
        }
        call(onChange, Object.assign({}, unifiedRect));
    }, [stateKey, designRect, dispatch, onChange]);
    const onDragStart = React.useCallback(() => {
        const rect = getCropRect();
        dragData.current = {
            initialRect: rect,
            aspectRatio: rect.width / rect.height,
        };
    }, [getCropRect]);
    const onHandleChanged = React.useCallback((handleId, point, downPoint, _, _evt) => {
        const data = dragData.current;
        if (!data) {
            return;
        }
        const { initialRect } = data;
        const pointerX = point.design.x;
        const pointerY = point.design.y;
        // const isShiftDown = evt?.shiftKey === true;
        // if(isShiftDown) {
        //     // aspect ratio
        //     let vec = Point.from(
        //         pointerX - downPoint.design.x,
        //         pointerY - downPoint.design.y
        //     ).normalized();
        // }
        const x = Math.max(0, Math.min(pointerX, designRect.width));
        const y = Math.max(0, Math.min(pointerY, designRect.height));
        const dx = x - downPoint.design.x;
        const dy = y - downPoint.design.y;
        let newRect;
        switch (handleId) {
            case 'drag':
                newRect = {
                    x: initialRect.x + dx,
                    y: initialRect.y + dy,
                    width: initialRect.width,
                    height: initialRect.height,
                };
                break;
            case 'nw':
                newRect = {
                    x: initialRect.x + dx,
                    y: initialRect.y + dy,
                    width: initialRect.width - dx,
                    height: initialRect.height - dy,
                };
                break;
            case 'ne':
                newRect = {
                    x: initialRect.x,
                    y: initialRect.y + dy,
                    width: initialRect.width + dx,
                    height: initialRect.height - dy,
                };
                break;
            case 'se':
                newRect = {
                    x: initialRect.x,
                    y: initialRect.y,
                    width: initialRect.width + dx,
                    height: initialRect.height + dy,
                };
                break;
            case 'sw':
                newRect = {
                    x: initialRect.x + dx,
                    y: initialRect.y,
                    width: initialRect.width - dx,
                    height: initialRect.height + dy,
                };
                break;
            case 'n':
                newRect = {
                    x: initialRect.x,
                    y: initialRect.y + dy,
                    width: initialRect.width,
                    height: initialRect.height - dy,
                };
                break;
            case 'w':
                newRect = {
                    x: initialRect.x + dx,
                    y: initialRect.y,
                    width: initialRect.width - dx,
                    height: initialRect.height,
                };
                break;
            case 's':
                newRect = {
                    x: initialRect.x,
                    y: initialRect.y,
                    width: initialRect.width,
                    height: initialRect.height + dy,
                };
                break;
            case 'e':
                newRect = {
                    x: initialRect.x,
                    y: initialRect.y,
                    width: initialRect.width + dx,
                    height: initialRect.height,
                };
                break;
            default:
                break;
        }
        if (newRect) {
            if (newRect.width < 10 || newRect.height < 10) {
                return;
            }
            const cropRect = {
                x: Math.max(0, Math.min(newRect.x, designRect.width - newRect.width)),
                y: Math.max(0, Math.min(newRect.y, designRect.height - newRect.height)),
                width: newRect.width,
                height: newRect.height,
            };
            setRect(cropRect);
        }
    }, [designRect, setRect]);
    const handlers = React.useMemo(() => ({
        onDragStart,
        onDrag: onHandleChanged,
        onDragEnd: onDragStart,
    }), [onDragStart, onHandleChanged]);
    useEffect(() => () => {
        if (stateKey) {
            dispatch(EditorActions.setControllerData({
                key: 'unifiedArea',
                value: undefined,
            }));
        }
    }, []);
    useHandleDrag(containerRef, handlers, {
        nodeClass: 'cropper-handle',
        idProp: 'data-action',
        workspace: 'design',
    });
    const rect = getCropRect();
    if (!rect) {
        return null;
    }
    return (React.createElement(View, { position: "absolute", left: 0, top: 0, width: "100%", height: "100%" },
        React.createElement("div", { ref: containerRef, className: "crop-container", style: {
                left: toPx(designRect.x),
                top: toPx(designRect.y),
                width: toPx(designRect.width),
                height: toPx(designRect.height),
            } },
            React.createElement(SVG, { width: "100%", height: "100%", className: "cropper-canvas", viewBox: `0 0 ${designRect.width} ${designRect.height}` },
                React.createElement("defs", null,
                    React.createElement("mask", { id: "cropper-mask" },
                        React.createElement("rect", { width: "100%", height: "100%", fill: "white" }),
                        React.createElement("rect", { x: rect.x.toFixed(2), y: rect.y.toFixed(2), width: rect.width.toFixed(2), height: rect.height.toFixed(2), fill: "black" }))),
                React.createElement("rect", { mask: "url(#cropper-mask)", width: "100%", height: "100%", fill: "black", fillOpacity: 0.45 })),
            React.createElement("div", { style: {
                    position: 'absolute',
                    left: toPx(rect.x),
                    top: toPx(rect.y),
                    width: toPx(rect.width),
                    height: toPx(rect.height),
                } },
                React.createElement("div", { className: "cropper-crop-box dashed-border" },
                    React.createElement(Handles, null))))));
};
